/**
 * Audio engine: plays sound ids from the sound table (bank.ts) through the shared mixer
 * (mixer.ts). Each id resolves to a sampled variant (sprites from samples.ts, picked at random,
 * never twice in a row); while a sprite is not loaded, or when it failed, the id's synthesized
 * recipe (recipes.ts) plays instead (or nothing). A recipe can still be layered under a sample
 * (`layer`), but the sound table no longer does: one sampled palette. Nothing throws.
 *
 * Mix rules applied here:
 *  - voice limiting: per id (`max`) and globally (VoiceLimiter: lowest priority / oldest stolen),
 *    plus a per-frame budget for world SFX (a 20-target AoE is one sound, not twenty);
 *  - rate limits per id (`gap`), e.g. UI errors;
 *  - priority / ducking: the local player's sounds route to the never-ducked "own" SFX input and
 *    get a priority boost; `duck` sounds (boss telegraph, your death, big hits on you) dip other
 *    units and the ambience;
 *  - per-play pitch / gain variation; stereo pan and distance low-pass from the caller.
 *
 * Settings: audio/settings.ts (master switch + bus volumes), applied by the mixer.
 */
import { SOUNDS, type SoundDef, variantsOf } from './bank';
import { mixer, type Mixer } from './mixer';
import { RECIPES, type Recipe, recipeDuration } from './recipes';
import { type SampleBank, samples } from './samples';
import { VoiceLimiter } from './voices';

/** Synth recipes were designed for the old 0.42 bus; this keeps them level with the samples. */
const SYNTH_TRIM = 0.4;
/** New world-SFX voices per frame. */
const MAX_PER_FRAME = 7;

export interface PlayOpts {
  /** Gain multiplier (default 1). */
  gain?: number;
  /** Stereo pan -1..1 (default 0). */
  pan?: number;
  /** Pitch multiplier (default 1, before jitter). */
  pitch?: number;
  /** Start delay in seconds (default 0). */
  delay?: number;
  /** Priority bonus for the limiter (the player's own sounds outrank the party's). */
  prioBoost?: number;
  /** The local player's own sound (never ducked). Default: prioBoost >= 1. */
  own?: boolean;
  /** Distance 0..1 (0 = at the listener): darkens the sound with a low-pass. */
  far?: number;
}

interface Live {
  out: GainNode;
  end: number;
}

export class CombatAudio {
  private noise: AudioBuffer | null = null;
  private readonly limiter = new VoiceLimiter(32, 3, 0.03);
  private readonly live = new Map<number, Live>();
  private readonly durations = new Map<string, number>();
  private readonly variants = new Map<string, string[]>();
  private readonly lastVariant = new Map<string, number>();
  private readonly lastAt = new Map<string, number>();
  private installed = false;
  private frameN = 0;
  /** Deterministic jitter in tests (defaults to Math.random). */
  rng: () => number = Math.random;

  constructor(
    readonly mix: Mixer = mixer,
    readonly bank: SampleBank = samples,
  ) {
    for (const [id, r] of Object.entries(RECIPES)) this.durations.set(id, recipeDuration(r));
    for (const def of Object.values(SOUNDS)) if (def.sample && !this.variants.has(def.sample)) this.variants.set(def.sample, variantsOf(def.sample));
  }

  /** Gesture / visibility / settings listeners and lazy sprite loading (idempotent). */
  install(): void {
    if (this.installed) return;
    this.installed = true;
    this.mix.install();
    // The UI sprite is tiny and needed on the first screen: decode it right away when possible.
    void this.bank.loadGroup('ui');
    this.mix.onReady((ctx) => {
      this.bank.attach(ctx);
      this.makeNoise(ctx);
      void this.bank.loadGroup('ui');
      // Then the rest of the palette, so world / combat sounds rarely need their synth fallback.
      void this.bank.loadGroup('meta').then(() => this.bank.loadGroup('combat'));
    });
  }

  /** Start loading a sprite group ('combat' when a run starts). Safe to call repeatedly. */
  preload(group: 'combat' | 'ui' | 'meta'): void {
    void this.bank.loadGroup(group);
  }

  get ready(): boolean {
    return this.mix.running;
  }

  /** Call once per rendered frame (resets the per-frame voice budget, picks up the first gesture). */
  beginFrame(): void {
    this.frameN = 0;
    this.mix.poll();
  }

  /** Length of a sound (s): its longest sample variant, else its recipe, else 0. */
  duration(id: string): number {
    const def = SOUNDS[id];
    const vs = def?.sample ? this.variants.get(def.sample) : undefined;
    if (vs?.length) {
      let d = 0;
      for (const k of vs) d = Math.max(d, this.bank.clip(k)?.dur ?? 0);
      if (d > 0) return d;
    }
    return this.durations.get(def?.synth ?? id) ?? 0;
  }

  /** True when `id` resolves to something playable (a sample variant or a recipe). */
  has(id: string): boolean {
    const def = SOUNDS[id];
    if (!def) return !!RECIPES[id];
    return (!!def.sample && (this.variants.get(def.sample)?.length ?? 0) > 0) || (!!def.synth && !!RECIPES[def.synth]);
  }

  private makeNoise(ctx: AudioContext): void {
    if (this.noise) return;
    try {
      // One second of white noise, shared by every synth noise layer (random start offsets).
      const len = ctx.sampleRate;
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.noise = buf;
    } catch {
      this.noise = null;
    }
  }

  /** Pick a sample variant (random, never the previous one) whose sprite is loaded. */
  private pickClip(def: SoundDef): ReturnType<SampleBank['clip']> {
    if (!def.sample) return null;
    const vs = this.variants.get(def.sample);
    if (!vs?.length) return null;
    const last = this.lastVariant.get(def.sample) ?? -1;
    let i = Math.floor(this.rng() * vs.length) % vs.length;
    if (vs.length > 1 && i === last) i = (i + 1) % vs.length;
    const c = this.bank.clip(vs[i]!);
    if (c) this.lastVariant.set(def.sample, i);
    return c;
  }

  /** Play sound `id`. Returns false when nothing played (muted, no gesture yet, limited, unknown). */
  play(id: string, opts: PlayOpts = {}): boolean {
    try {
      return this.playInner(id, opts);
    } catch {
      return false;
    }
  }

  private playInner(id: string, opts: PlayOpts): boolean {
    const def: SoundDef | undefined = SOUNDS[id] ?? (RECIPES[id] ? { bus: 'sfx', prio: RECIPES[id]!.prio, synth: id } : undefined);
    if (!def) return false;
    if (!this.mix.enabled || this.mix.level(def.bus) <= 0.0005) return false;
    const ctx = this.mix.ctx;
    if (!ctx || ctx.state !== 'running') return false;
    if (typeof document !== 'undefined' && document.hidden) return false;
    const world = def.bus === 'sfx';
    if (world && this.frameN >= MAX_PER_FRAME) return false;
    const now = ctx.currentTime;
    const delay = Math.max(0, opts.delay ?? 0);
    if (def.gap) {
      const last = this.lastAt.get(id);
      if (last !== undefined && now + delay - last < def.gap) return false;
    }
    const clip = this.pickClip(def);
    const recipe: Recipe | undefined = def.synth ? RECIPES[def.synth] : undefined;
    const synthGain = clip ? (def.layer ?? 0) : recipe ? 1 : 0;
    if (!clip && !synthGain) return false;
    const rnd = this.rng;
    const pitch = Math.max(0.25, (opts.pitch ?? 1) * (1 + (rnd() - 0.5) * 2 * (def.pitch ?? 0.045)));
    const dur = Math.max(clip ? clip.dur / pitch : 0, synthGain && recipe ? (this.durations.get(def.synth!) ?? 0) / pitch : 0);
    const adm = this.limiter.admit(id, def.prio + (opts.prioBoost ?? 0), now + delay, dur + 0.02, def.max ?? 3);
    if (adm.id < 0) return false;
    if (adm.stolen >= 0) this.stop(adm.stolen, now);
    if (world) this.frameN++;
    this.lastAt.set(id, now + delay);
    this.sweep(now);

    const own = opts.own ?? (opts.prioBoost ?? 0) >= 1;
    const bus = this.mix.input(def.bus, own);
    if (!bus) return false;
    const gain = (opts.gain ?? 1) * (def.gain ?? 1) * (1 + (rnd() - 0.5) * 2 * (def.vary ?? 0.09));
    const out = ctx.createGain();
    out.gain.value = Math.max(0, gain);
    let tailNode: AudioNode = out;
    const far = Math.max(0, Math.min(1, opts.far ?? 0));
    if (far > 0.15) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 16000 * (1 - 0.8 * far);
      tailNode.connect(lp);
      tailNode = lp;
    }
    const pan = Math.max(-1, Math.min(1, opts.pan ?? 0));
    if (Math.abs(pan) > 0.02 && typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      tailNode.connect(p);
      tailNode = p;
    }
    tailNode.connect(bus);
    const t0 = now + 0.004 + delay;
    if (clip) {
      const src = ctx.createBufferSource();
      src.buffer = clip.buffer;
      src.playbackRate.value = pitch;
      src.connect(out);
      src.start(t0, clip.start, clip.dur);
      src.onended = (): void => {
        try {
          out.disconnect();
        } catch {
          /* already gone */
        }
      };
    }
    if (synthGain > 0 && recipe && this.noise) this.synth(ctx, recipe, out, t0, pitch, synthGain * SYNTH_TRIM);
    this.live.set(adm.id, { out, end: t0 + dur + 0.05 });
    if (def.duck && (own || def.prio >= 7)) this.mix.duck(def.duck, Math.min(0.9, Math.max(0.25, dur)));
    return true;
  }

  /** Render a recipe into `out` (procedural layers: filtered noise and tones). */
  private synth(ctx: AudioContext, recipe: Recipe, out: GainNode, t0: number, pitch: number, level: number): void {
    const rnd = this.rng;
    const g = level * (recipe.gain ?? 1);
    for (const l of recipe.layers) {
      const start = t0 + (l.t ?? 0);
      const a = Math.max(0.001, l.a ?? 0.003);
      const end = start + Math.max(a + 0.005, l.d);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, start);
      env.gain.linearRampToValueAtTime(Math.max(0.0002, l.g * g), start + a);
      env.gain.exponentialRampToValueAtTime(0.0001, end);
      env.connect(out);
      if (l.k === 'tone') {
        const osc = ctx.createOscillator();
        osc.type = l.w ?? 'sine';
        const f = l.f * pitch;
        osc.frequency.setValueAtTime(f, start);
        if (l.f1) osc.frequency.exponentialRampToValueAtTime(Math.max(20, l.f1 * pitch), end);
        if (l.fm && l.fmDepth) {
          const mod = ctx.createOscillator();
          mod.frequency.value = f * l.fm;
          const mg = ctx.createGain();
          mg.gain.setValueAtTime(l.fmDepth * pitch, start);
          mg.gain.exponentialRampToValueAtTime(Math.max(0.01, l.fmDepth * 0.05), end);
          mod.connect(mg).connect(osc.frequency);
          mod.start(start);
          mod.stop(end + 0.02);
        }
        osc.connect(env);
        osc.start(start);
        osc.stop(end + 0.02);
      } else {
        const src = ctx.createBufferSource();
        src.buffer = this.noise;
        src.loop = true;
        const filt = ctx.createBiquadFilter();
        filt.type = l.filter;
        const f = Math.min(18000, l.f * pitch);
        filt.frequency.setValueAtTime(f, start);
        if (l.f1) filt.frequency.exponentialRampToValueAtTime(Math.min(18000, Math.max(30, l.f1 * pitch)), end);
        filt.Q.value = l.q ?? (l.filter === 'bandpass' ? 1.2 : 0.7);
        src.connect(filt).connect(env);
        src.start(start, rnd() * 0.9);
        src.stop(end + 0.02);
      }
    }
  }

  /** Fade a stolen voice out fast (its sources stop on their own schedule). */
  private stop(id: number, now: number): void {
    const v = this.live.get(id);
    if (!v) return;
    v.out.gain.cancelScheduledValues(now);
    v.out.gain.setTargetAtTime(0, now, 0.012);
    this.live.delete(id);
  }

  private sweep(now: number): void {
    if (this.live.size < 48) return;
    for (const [id, v] of this.live) if (v.end < now) this.live.delete(id);
  }
}

/** The shared audio engine (combat, world, UI, stingers). */
export const combatAudio = new CombatAudio();

/**
 * Play a sound id from the sound table (bank.ts) anywhere in the app, e.g.
 * `playSound('ui.chat')`, `playSound('guild.recruit')`, `playSound('idle.tick')`.
 * Safe before the first gesture, while muted, or when assets failed (it just returns false).
 */
export function playSound(id: string, opts?: PlayOpts): boolean {
  combatAudio.install();
  return combatAudio.play(id, opts);
}
